import { useState, useEffect, useCallback, useRef } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search, Plus, User, Phone, Mail, Droplets, AlertTriangle, Edit, Trash2, Loader2, CheckCircle, UserCheck,
  Stethoscope, Pill, FlaskConical, Scissors, Calendar, FileText, Download, Upload, File, Image, TrendingUp, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { openPrescriptionPrint } from '../components/PrescriptionTemplate';
import BiomarkerTrendChart from '../components/BiomarkerTrendChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [editingPatient, setEditingPatient] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [portalPatientFound, setPortalPatientFound] = useState(false);
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [clinicSettings, setClinicSettings] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [form, setForm] = useState({
    name: '', age: '', gender: '', phone: '', email: '',
    address: '', blood_group: '', allergies: '', abha_id: ''
  });
  const [editForm, setEditForm] = useState({
    name: '', age: '', gender: '', phone: '', email: '',
    address: '', blood_group: '', allergies: '', abha_id: ''
  });

  const fetchPatients = async () => {
    try {
      const res = await API.get(`/patients?search=${search}`);
      setPatients(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const timer = setTimeout(fetchPatients, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    API.get('/clinic-settings').then(r => setClinicSettings(r.data)).catch(console.error);
  }, []);

  // Fetch full visit history when a patient's detail panel is opened
  useEffect(() => {
    if (!selectedPatient) { setVisits([]); return; }
    setLoadingVisits(true);
    API.get(`/clinical/patient/${selectedPatient.id}`)
      .then(r => setVisits(r.data || []))
      .catch(err => console.error(err))
      .finally(() => setLoadingVisits(false));
  }, [selectedPatient]);

  const handlePrintRx = (visit, patient) => {
    openPrescriptionPrint(visit, patient, clinicSettings);
  };

  const handleExportPatientPdf = async () => {
    if (!selectedPatient) return;
    setExportingPdf(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/reports/patient-records-pdf/${selectedPatient.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `patient_record_${selectedPatient.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Patient record exported');
    } catch (err) { toast.error('Export failed'); }
    finally { setExportingPdf(false); }
  };

  // Auto-refresh patients list every 30s
  const patPollRef = useRef(null);
  useEffect(() => {
    patPollRef.current = setInterval(() => { if (!document.hidden) fetchPatients(); }, 30000);
    return () => clearInterval(patPollRef.current);
  }, []);
  
  // Lookup patient from Portal when phone/email changes
  const lookupPortalPatient = useCallback(async (phone, email) => {
    if (!phone && !email) return;
    if (phone && phone.length < 10) return;
    
    setLookingUp(true);
    try {
      const params = new URLSearchParams();
      if (phone) params.append('phone', phone);
      if (email) params.append('email', email);
      
      const res = await API.get(`/patients/lookup-portal-patient?${params}`);
      if (res.data.found && res.data.patient) {
        const p = res.data.patient;
        setForm(prev => ({
          ...prev,
          name: p.name || prev.name,
          email: p.email || prev.email,
          phone: p.phone?.replace('+91', '') || prev.phone,
          gender: p.gender || prev.gender,
          blood_group: p.blood_group || prev.blood_group,
          address: p.address || prev.address,
          allergies: p.allergies || prev.allergies
        }));
        setPortalPatientFound(true);
        toast.success('Patient found in Patient Portal! Data auto-filled.');
      } else {
        setPortalPatientFound(false);
      }
    } catch (err) {
      console.error('Portal lookup failed:', err);
      setPortalPatientFound(false);
    }
    setLookingUp(false);
  }, []);
  
  // Debounced lookup on phone/email change
  useEffect(() => {
    if (!dialogOpen) return;
    const timer = setTimeout(() => {
      if (form.phone?.length >= 10 || (form.email && form.email.includes('@'))) {
        lookupPortalPatient(form.phone, form.email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.phone, form.email, dialogOpen, lookupPortalPatient]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        age: parseInt(form.age) || 0,
        allergies: form.allergies ? form.allergies.split(',').map(a => a.trim()) : []
      };
      await API.post('/patients', data);
      toast.success('Patient added');
      setDialogOpen(false);
      setForm({ name: '', age: '', gender: '', phone: '', email: '', address: '', blood_group: '', allergies: '', abha_id: '' });
      setPortalPatientFound(false);
      fetchPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const openEditDialog = (patient) => {
    setEditingPatient(patient);
    setEditForm({
      name: patient.name || '',
      age: patient.age?.toString() || '',
      gender: patient.gender || '',
      phone: patient.phone || '',
      email: patient.email || '',
      address: patient.address || '',
      blood_group: patient.blood_group || '',
      allergies: (patient.allergies || []).join(', '),
      abha_id: patient.abha_id || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingPatient) return;
    try {
      const data = {
        ...editForm,
        age: parseInt(editForm.age) || 0,
        allergies: editForm.allergies ? editForm.allergies.split(',').map(a => a.trim()) : []
      };
      await API.put(`/patients/${editingPatient.id}`, data);
      toast.success('Patient updated');
      setEditDialogOpen(false);
      setEditingPatient(null);
      setSelectedPatient(null);
      fetchPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (patient) => {
    setDeleting(true);
    try {
      await API.delete(`/patients/${patient.id}`);
      toast.success(`${patient.name} deleted`);
      setSelectedPatient(null);
      fetchPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="patients-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{fontFamily:'Manrope'}}>Patients</h1>
          <p className="text-sm text-slate-500">{patients.length} registered patients</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white h-11" data-testid="patients-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Patient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{fontFamily:'Manrope'}}>New Patient</DialogTitle>
            </DialogHeader>
            {portalPatientFound && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                <UserCheck className="w-5 h-5" />
                <span>Patient found in Patient Portal - Data auto-filled!</span>
              </div>
            )}
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone {lookingUp && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}</Label>
                  <Input data-testid="patient-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Enter 10-digit mobile" className="h-12 mt-1" />
                  <p className="text-xs text-slate-400 mt-1">Enter phone to auto-fetch from Patient Portal</p>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="patient@email.com" className="h-12 mt-1" />
                </div>
                <div className="col-span-2">
                  <Label>Full Name *</Label>
                  <Input data-testid="patient-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-12 mt-1" required />
                </div>
                <div>
                  <Label>Age</Label>
                  <Input data-testid="patient-age-input" type="number" value={form.age} onChange={e => setForm({...form, age: e.target.value})} className="h-12 mt-1" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={form.gender} onValueChange={v => setForm({...form, gender: v})}>
                    <SelectTrigger className="h-12 mt-1" data-testid="patient-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Blood Group</Label>
                  <Select value={form.blood_group} onValueChange={v => setForm({...form, blood_group: v})}>
                    <SelectTrigger className="h-12 mt-1" data-testid="patient-blood-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                        <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Allergies (comma-separated)</Label>
                  <Input data-testid="patient-allergies-input" value={form.allergies} onChange={e => setForm({...form, allergies: e.target.value})} placeholder="Penicillin, Aspirin" className="h-12 mt-1" />
                </div>
                <div className="col-span-2">
                  <Label>ABHA ID (optional)</Label>
                  <Input data-testid="patient-abha-input" value={form.abha_id} onChange={e => setForm({...form, abha_id: e.target.value})} placeholder="14-digit ABHA number" className="h-12 mt-1" />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" data-testid="patient-submit-btn">
                Add Patient
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="patients-search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or phone..."
          className="h-12 pl-10"
        />
      </div>

      {/* Patient List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="patients-list">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-teal-700 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : patients.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <User className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">{search ? 'No patients found' : 'No patients yet'}</p>
          </div>
        ) : (
          patients.map((patient, i) => (
            <Card
              key={patient.id}
              className="card-hover border-slate-200 dark:border-slate-800 cursor-pointer"
              onClick={() => setSelectedPatient(patient)}
              data-testid={`patient-card-${i}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center shrink-0">
                    <span className="font-bold text-teal-700 dark:text-teal-400 text-sm">{patient.name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-900 dark:text-white truncate">{patient.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {patient.age > 0 && <span className="text-xs text-slate-500">{patient.age}y</span>}
                      {patient.gender && <span className="text-xs text-slate-500">{patient.gender}</span>}
                      {patient.blood_group && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5">
                          <Droplets className="w-2.5 h-2.5 mr-0.5 text-red-500" /> {patient.blood_group}
                        </Badge>
                      )}
                    </div>
                    {patient.phone && (
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {patient.phone}
                      </p>
                    )}
                    {patient.allergies?.length > 0 && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                        <span className="text-[10px] text-amber-600">{patient.allergies.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Patient Detail Sheet */}
      <Sheet open={!!selectedPatient} onOpenChange={() => setSelectedPatient(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle style={{fontFamily:'Manrope'}}>{selectedPatient?.name}</SheetTitle>
          </SheetHeader>
          {selectedPatient && (
            <div className="mt-6 space-y-6">
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="w-full flex">
                  <TabsTrigger value="details" data-testid="patient-detail-tab-details" className="flex-1 text-xs sm:text-sm">Details</TabsTrigger>
                  <TabsTrigger value="visits" data-testid="patient-detail-tab-visits" className="flex-1 text-xs sm:text-sm">
                    <Stethoscope className="w-3.5 h-3.5 mr-1" /> History ({visits.length})
                  </TabsTrigger>
                  <TabsTrigger value="trends" data-testid="patient-detail-tab-trends" className="flex-1 text-xs sm:text-sm">
                    <TrendingUp className="w-3.5 h-3.5 mr-1" /> Trends
                  </TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="space-y-6 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Age', value: selectedPatient.age ? `${selectedPatient.age} years` : '-' },
                      { label: 'Gender', value: selectedPatient.gender || '-' },
                      { label: 'Phone', value: selectedPatient.phone || '-' },
                      { label: 'Blood Group', value: selectedPatient.blood_group || '-' },
                      { label: 'ABHA ID', value: selectedPatient.abha_id || '-' },
                      { label: 'Email', value: selectedPatient.email || '-' },
                    ].map(item => (
                      <div key={item.label}>
                        <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{item.value}</p>
                      </div>
                    ))}
                  </div>
                  {selectedPatient.allergies?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-400 mb-2">Allergies</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedPatient.allergies.map(a => (
                          <Badge key={a} className="bg-red-50 text-red-600 border-red-100 text-xs">{a}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Visits / Full History Tab */}
                <TabsContent value="visits" className="mt-4">
                  <div className="flex justify-end mb-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleExportPatientPdf}
                      disabled={exportingPdf}
                      data-testid="export-patient-pdf-btn"
                    >
                      {exportingPdf ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                      Export PDF
                    </Button>
                  </div>
                  {loadingVisits ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-5 h-5 animate-spin text-teal-700" />
                    </div>
                  ) : visits.length === 0 ? (
                    <div className="text-center py-16">
                      <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-400 text-sm">No visits recorded yet</p>
                    </div>
                  ) : (
                    <div data-testid="patient-detail-history-timeline">
                      {visits.map((visit, i) => (
                        <div key={visit.id} className="relative pl-8 pb-6" data-testid={`patient-detail-visit-${i}`}>
                          <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center z-10">
                            <Stethoscope className="w-3 h-3 text-teal-700" />
                          </div>
                          {i < visits.length - 1 && <div className="absolute left-[11px] top-7 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />}

                          <Card className="border-slate-100 dark:border-slate-800">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                  <span className="text-xs text-slate-500">{new Date(visit.created_at).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                </div>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-teal-600" onClick={() => handlePrintRx(visit, selectedPatient)} data-testid={`patient-detail-print-rx-${i}`}>
                                  <Download className="w-3 h-3 mr-1" /> Rx
                                </Button>
                              </div>

                              {visit.diagnosis?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {visit.diagnosis.map(d => <Badge key={d} className="bg-teal-50 text-teal-700 text-[10px]">{d}</Badge>)}
                                </div>
                              )}

                              {visit.subjective && <p className="text-sm text-slate-600 dark:text-slate-300 mb-1"><strong className="text-teal-700">S:</strong> {visit.subjective}</p>}
                              {visit.assessment && <p className="text-sm text-slate-600 dark:text-slate-300 mb-1"><strong className="text-teal-700">A:</strong> {visit.assessment}</p>}

                              {visit.medications?.length > 0 && (
                                <div className="mt-2">
                                  <div className="flex items-center gap-1 mb-1">
                                    <Pill className="w-3 h-3 text-blue-500" />
                                    <span className="text-[10px] font-semibold text-slate-500 uppercase">Medications</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {visit.medications.filter(m => m.name).map((m, j) => (
                                      <Badge key={j} variant="outline" className="text-[10px]">{m.name} {m.dosage}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {visit.lab_tests?.length > 0 && (
                                <div className="mt-2">
                                  <div className="flex items-center gap-1 mb-1">
                                    <FlaskConical className="w-3 h-3 text-purple-500" />
                                    <span className="text-[10px] font-semibold text-slate-500 uppercase">Lab Tests</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {visit.lab_tests.map((t, j) => <Badge key={j} variant="outline" className="text-[10px]">{t.name}</Badge>)}
                                  </div>
                                </div>
                              )}

                              {visit.treatments?.length > 0 && (
                                <div className="mt-2">
                                  <div className="flex items-center gap-1 mb-1">
                                    <Scissors className="w-3 h-3 text-amber-500" />
                                    <span className="text-[10px] font-semibold text-slate-500 uppercase">Treatment/Surgery</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {visit.treatments.map((t, j) => <Badge key={j} className="bg-amber-50 text-amber-700 text-[10px]">{t.name}</Badge>)}
                                  </div>
                                </div>
                              )}

                              {visit.prescription_barcode && (
                                <div className="mt-2 bg-slate-50 border border-slate-200 p-2 rounded text-center" data-testid={`patient-detail-rx-barcode-${i}`}>
                                  <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(visit.prescription_barcode)}`}
                                    alt="Rx QR"
                                    className="mx-auto mb-1"
                                    style={{ width: 60, height: 60 }}
                                  />
                                  <span className="text-[10px] text-slate-500">Rx Code: </span>
                                  <span className="text-xs font-mono font-bold tracking-wider">{visit.prescription_barcode}</span>
                                </div>
                              )}

                              {visit.attachments?.length > 0 && (
                                <div className="mt-3 pt-2 border-t border-slate-100">
                                  <div className="flex items-center gap-1 mb-2">
                                    <Upload className="w-3 h-3 text-blue-500" />
                                    <span className="text-[10px] font-semibold text-slate-500 uppercase">Visit Attachments ({visit.attachments.length})</span>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {visit.attachments.map((att, j) => (
                                      <a
                                        key={j}
                                        href={`${BACKEND_URL}${att.file_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1.5 rounded-lg transition-colors"
                                        data-testid={`patient-detail-attachment-${i}-${j}`}
                                      >
                                        {att.file_type?.startsWith('image/') ? <Image className="w-3.5 h-3.5" /> : <File className="w-3.5 h-3.5" />}
                                        <span className="max-w-[150px] truncate">{att.file_name}</span>
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {visit.follow_up_date && (
                                <p className="text-xs text-amber-600 mt-2">Follow-up: {visit.follow_up_date}</p>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Trends Tab */}
                <TabsContent value="trends" className="mt-4">
                  <BiomarkerTrendChart
                    patientId={selectedPatient.id}
                    patientName={selectedPatient.name}
                  />
                </TabsContent>
              </Tabs>

              {/* Edit and Delete Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => openEditDialog(selectedPatient)}
                  data-testid="patient-edit-btn"
                >
                  <Edit className="w-4 h-4 mr-2" /> Edit Details
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="patient-delete-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Patient?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete <strong>{selectedPatient.name}</strong> and all their related records (appointments, visits, invoices). This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => handleDelete(selectedPatient)}
                        disabled={deleting}
                      >
                        {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Delete Patient
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Patient Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Manrope'}}>Edit Patient</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input data-testid="edit-patient-name-input" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="h-12 mt-1" required />
              </div>
              <div>
                <Label>Age</Label>
                <Input data-testid="edit-patient-age-input" type="number" value={editForm.age} onChange={e => setEditForm({...editForm, age: e.target.value})} className="h-12 mt-1" />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={editForm.gender} onValueChange={v => setEditForm({...editForm, gender: v})}>
                  <SelectTrigger className="h-12 mt-1" data-testid="edit-patient-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="edit-patient-phone-input" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} placeholder="+91" className="h-12 mt-1" />
              </div>
              <div>
                <Label>Blood Group</Label>
                <Select value={editForm.blood_group} onValueChange={v => setEditForm({...editForm, blood_group: v})}>
                  <SelectTrigger className="h-12 mt-1" data-testid="edit-patient-blood-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Allergies (comma-separated)</Label>
                <Input data-testid="edit-patient-allergies-input" value={editForm.allergies} onChange={e => setEditForm({...editForm, allergies: e.target.value})} placeholder="Penicillin, Aspirin" className="h-12 mt-1" />
              </div>
              <div className="col-span-2">
                <Label>ABHA ID (optional)</Label>
                <Input data-testid="edit-patient-abha-input" value={editForm.abha_id} onChange={e => setEditForm({...editForm, abha_id: e.target.value})} placeholder="14-digit ABHA number" className="h-12 mt-1" />
              </div>
            </div>
            <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" data-testid="edit-patient-submit-btn">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
